// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.

/**
 * Centralized helper to update async request status, data, and errors.
 *
 * @param {Object} state - Redux slice state
 * @param {string} key - State key being updated
 * @param {"loading" | "succeeded" | "failed"} type - Request status
 * @param {Object|null} action - Redux action (contains payload)
 * @param {boolean} updateData - Whether to update state[key] with payload
 *                              (false when websocket is the source of truth)
 */
export const updateStateStatus = (state, key, type, action = null, updateData = true) => {
  state.status[key] = type;

  if (type === 'succeeded') {
    if (updateData && action?.payload !== undefined) {
      state[key] = action.payload;
    }
    state.error[key] = null;
  }
  if (type === 'failed') {
    state.error[key] = action?.payload;
  }
};
/**
 * Utility to attach common pending/fulfilled/rejected cases
 * for multiple async thunks in a Redux slice.
 *
 * @param {Object} builder - Redux Toolkit builder
 * @param {Array} actions - List of async actions and state keys
 */
export const addAsyncCases = (builder, actions) => {
  actions.forEach(({ action, key, updateData = true }) => {
    builder
      .addCase(action.pending, state => updateStateStatus(state, key, 'loading'))
      .addCase(action.fulfilled, (state, result) =>
        updateStateStatus(state, key, 'succeeded', result, updateData)
      )
      .addCase(action.rejected, (state, result) => updateStateStatus(state, key, 'failed', result));
  });
};

/**
 * Standardized API call wrapper for async thunks.
 * Handles response extraction and normalizes error messages
 *
 * @param {Function} request - Function that performs the API request
 * @param {Function} rejectWithValue - RTK helper for rejected actions
 */

export const apiCall = async (request, rejectWithValue) => {
  try {
    const response = await request();
    return response?.data ?? null;
  } catch (error) {
    const detail = error?.response?.data?.detail;
    let errorMessage;
    // Check if detail is an array object or a string and deal with the variety of possible
    // error messages.
    if (Array.isArray(detail)) {
      errorMessage =
        detail[0]?.msg ||
        detail[0]?.error ||
        detail[0]?.message ||
        detail[0] ||
        error?.message ||
        error?.msg ||
        'An error occurred';
    } else if ((typeof detail === 'object' && detail !== null) || typeof detail === 'string') {
      errorMessage = detail;
    } else {
      errorMessage = error?.message || 'An error occurred';
    }

    return rejectWithValue(errorMessage);
  }
};

/**
 * =====================
 * Generic state management helpers
 * =====================
 */

/** Array helpers */
export const addToArray = (array, item) => {
  array.push(item);
};

export const updateArrayItemByKey = (array, key, updatedItem) =>
  array.map(item => (item[key] === updatedItem[key] ? { ...item, ...updatedItem } : item));

export const removeArrayItemByKey = (array, key, uuid) => array.filter(item => item[key] !== uuid);

/** Map helpers */
export const upsertByKey = (map, key, entity) => {
  map[key] = {
    ...(map[key] || {}),
    ...entity,
  };
};

export const removeByKey = (map, key) => {
  delete map[key];
};
